import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { AlertOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { useToggle } from 'ahooks';
import { message, Modal, Upload } from 'antd';
import { UploadChangeParam, UploadProps } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { useAuthorization } from 'components/Authorization';
import { ImageInfo } from '../types';

const styles = {
  container: css`
    margin-left: 8px;
    .ant-upload-list-picture-card {
      .ant-upload-select-picture-card {
        height: 72px !important;
        width: 72px !important;
      }
      .ant-upload-list-picture-card-container {
        height: 72px !important;
        width: 72px !important;
        .ant-upload-list-item-uploading {
          font-size: 12px;
          display: flex;
          flex-direction: column;
        }
      }
    }
  `,
  uploadButton: css`
    margin-top: 8px;
  `,
  imageTip: css`
    font-size: 10px;
    padding-left: 12px;
  `,
  redText: css`
    color: red;
    margin: 0 4px;
  `,
};

interface ImageUploadResponse {
  data: {
    url: string;
  };
}

const UploadSizeLimit = 2 * 1024 * 1024;

interface Props extends UploadProps {
  fileSource: ImageInfo[];
  onImagesChange: (images: ImageInfo[]) => void;
}

const FooterImagePlugin = (props: Props) => {
  const { className, style, fileSource, maxCount, disabled, onImagesChange, ...otherProps } = props;

  const { token } = useAuthorization();

  const { t } = useTranslate();

  const [fileList, setFileList] = useState<UploadFile<ImageUploadResponse>[]>(() =>
    fileSource.map(({ imageId, size, status, error, originFileObj, ...others }, index) => {
      if (error && status === 'error') {
        if (error.status === 401) {
          // eslint-disable-next-line no-param-reassign
          error.message = t('upload.authorizeFailed', {
            name: originFileObj.name ?? '',
          });
        } else {
          // eslint-disable-next-line no-param-reassign
          error.message = t('upload.uploadFailed', {
            name: originFileObj.name ?? '',
          });
        }
      }
      return {
        ...others,
        type: 'image',
        uid: `${imageId}#${index}`,
        name: status === 'error' ? t('upload.failed') : `${imageId}#${index}`,
        url: imageId,
        thumbUrl: imageId,
        size: size || 10000,
        status: status || (imageId ? ('done' as const) : ('error' as const)),
        originFileObj,
        error,
        imageId,
      };
    }),
  );

  const [previewModalVisible, { setLeft: closePreview, setRight: openPreview }] = useToggle();
  const [previewImgUrl, setPreviewImgUrl] = useState<string | undefined>();

  const showUploadButton = useMemo(() => {
    if (!disabled && maxCount && maxCount <= fileSource.length) {
      return false;
    }
    return true;
  }, [fileSource.length, maxCount, disabled]);

  const isAllImageResolve = useMemo(
    () => !fileList.map((file) => file.status).includes('uploading'),
    [fileList],
  );

  const handleChange = useCallback(
    (uploadChangeParam: UploadChangeParam<UploadFile<ImageUploadResponse>>) => {
      if (!disabled) {
        const { fileList } = uploadChangeParam;
        setFileList(fileList);
      }
    },
    [disabled],
  );

  const handlePreview = useCallback(
    (file: UploadFile<ImageUploadResponse>) => {
      if (file.response) {
        setPreviewImgUrl(file.response?.data.url);
        openPreview();
      } else if (file.url) {
        setPreviewImgUrl(file.url);
        openPreview();
      } else {
        message.error(t('upload.previewFailed'));
      }
    },
    [openPreview, t],
  );

  const refs = useRef({
    onImagesChange,
  });
  refs.current = {
    onImagesChange,
  };

  useEffect(() => {
    if (isAllImageResolve) {
      const {
        current: { onImagesChange },
      } = refs;

      onImagesChange(
        fileList.map((file, index) => {
          const { response, ...others } = file as UploadFile<ImageUploadResponse>;
          return {
            index,
            imageId: response?.data?.url,
            ...others,
          };
        }),
      );
    }
  }, [fileList, isAllImageResolve]);

  return (
    <div className={className} style={style}>
      <Upload
        className={styles.container}
        accept=".jpg,.jpeg,.png"
        name="file"
        headers={{
          [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
        }}
        listType="picture-card"
        fileList={fileList}
        beforeUpload={useCallback(
          (file) => {
            if (file.size > UploadSizeLimit) {
              message.warn(
                t('upload.limitWarn', {
                  size: `${(UploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
                }),
              );
              return Promise.reject(Error('image over size'));
            }

            return Promise.resolve();
          },
          [t],
        )}
        onPreview={handlePreview}
        onChange={handleChange}
        {...otherProps}
      >
        {showUploadButton && (
          <div>
            <PlusOutlined />
            <div className={styles.uploadButton}>{t('upload.upload')}</div>
          </div>
        )}
      </Upload>
      <div className={styles.imageTip}>
        <AlertOutlined />
        <span className={styles.redText}>{t('upload.attentions')}</span>
        {t('upload.limit')}
      </div>
      <Modal
        visible={previewModalVisible}
        title={t('upload.preview')}
        footer={null}
        onCancel={closePreview}
      >
        <img alt="example" style={{ width: '100%' }} src={previewImgUrl} />
      </Modal>
    </div>
  );
};

export default FooterImagePlugin;
